import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_post-planner-51/artifacts/17buinbo_AFA%20transparent.png";

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="login-page">
      {/* Left Panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Post Planner" className="h-10 w-10 object-contain" />
            <span className="font-bold text-xl text-[#1a3a4a]">Post Planner</span>
          </div>

          <h1 className="text-3xl font-black text-[#1a3a4a] tracking-tight mb-2">Welcome back</h1>
          <p className="text-[#1a3a4a]/60 mb-8">Sign in to continue to your calendar</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="border-[#1a3a4a]/15 focus:border-[#c5d93d] h-12"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#2b7a8a] mb-2 block">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="border-[#1a3a4a]/15 focus:border-[#c5d93d] h-12 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1a3a4a]/40 hover:text-[#1a3a4a]/60"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary h-12 rounded-md text-base"
              data-testid="login-submit-btn"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

        </div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex lg:flex-1 bg-[#1a3a4a] items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-24 h-24 bg-[#c5d93d]/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <img src={LOGO_URL} alt="" className="w-16 h-16 object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Plan Your Social Content</h2>
          <p className="text-white/60 leading-relaxed">
            Organize your social media posts by month, import from CSV, and share beautiful calendars with your clients.
          </p>
        </div>
      </div>
    </div>
  );
}
